import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Upload, Search, Trash2, Pencil, Loader2, Phone, Mail, Tag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { api } from '@/lib/api';
import { toast } from 'sonner';

function ContactDialog({ open, onOpenChange, contact, onSaved }) {
  const [form, setForm] = useState({ name: '', phone: '', email: '', notes: '', tags: '' });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (contact) {
      setForm({
        name: contact.name || '',
        phone: contact.phone || '',
        email: contact.email || '',
        notes: contact.notes || '',
        tags: (contact.tags || []).join(', '),
      });
    } else {
      setForm({ name: '', phone: '', email: '', notes: '', tags: '' });
    }
  }, [contact, open]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        phone: form.phone,
        email: form.email || null,
        notes: form.notes || null,
        tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
      };
      if (contact?.id) {
        await api.put(`/contacts/${contact.id}`, payload);
        toast.success('Contact updated');
      } else {
        await api.post('/contacts/', payload);
        toast.success('Contact added');
      }
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-strong border-white/40 sm:max-w-md rounded-2xl">
        <DialogHeader>
          <DialogTitle>{contact ? 'Edit Contact' : 'New Contact'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5">
            <Label>Name</Label>
            <Input data-testid="contact-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="bg-white/70 border-white/60 rounded-xl" />
          </div>
          <div className="space-y-1.5">
            <Label>Phone (with country code)</Label>
            <Input data-testid="contact-phone-input" value={form.phone} placeholder="+919876543210" onChange={(e) => setForm({ ...form, phone: e.target.value })} required className="bg-white/70 border-white/60 rounded-xl" />
            <div className="text-[11px] text-muted-foreground">10-digit Indian numbers will be auto-prefixed with +91.</div>
          </div>
          <div className="space-y-1.5">
            <Label>Email (optional)</Label>
            <Input data-testid="contact-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-white/70 border-white/60 rounded-xl" />
          </div>
          <div className="space-y-1.5">
            <Label>Tags (comma-separated)</Label>
            <Input data-testid="contact-tags-input" value={form.tags} placeholder="vip, goa, repeat" onChange={(e) => setForm({ ...form, tags: e.target.value })} className="bg-white/70 border-white/60 rounded-xl" />
          </div>
          <div className="space-y-1.5">
            <Label>Notes</Label>
            <Textarea data-testid="contact-notes-input" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-white/70 border-white/60 rounded-xl" />
          </div>
          <DialogFooter className="gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-xl bg-white/60 border-white/60">Cancel</Button>
            <Button type="submit" disabled={saving} className="gradient-orange text-white rounded-xl" data-testid="contact-save-button">
              {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CSVImportDialog({ open, onOpenChange, onImported }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const onUpload = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/contacts/import-csv', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(r.data);
      toast.success(`Imported ${r.data.added} contacts`);
      onImported?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'CSV import failed');
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    setFile(null);
    setResult(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="glass-strong border-white/40 sm:max-w-lg rounded-2xl">
        <DialogHeader>
          <DialogTitle>Import contacts from CSV</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-sm text-muted-foreground">
            Expected headers (case-insensitive): <span className="font-medium">name, phone, email, tags</span>. Tags can be separated by <code>,</code> <code>;</code> <code>|</code>.
          </div>
          <input
            ref={fileRef}
            type="file"
            accept=".csv"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            data-testid="contacts-csv-file-input"
            className="block w-full text-sm rounded-xl border border-white/60 bg-white/60 p-2.5 file:mr-3 file:rounded-lg file:border-0 file:bg-orange-50 file:px-3 file:py-1.5 file:text-orange-800"
          />
          {result && (
            <div className="glass p-3 rounded-xl text-sm">
              <div>✅ Added: <span className="font-semibold">{result.added}</span></div>
              <div>↗️ Skipped (duplicates / errors): <span className="font-semibold">{result.skipped}</span></div>
              {result.errors?.length > 0 && (
                <details className="mt-1">
                  <summary className="cursor-pointer">Show first {result.errors.length} issues</summary>
                  <ul className="mt-1 max-h-40 overflow-auto text-xs text-muted-foreground">
                    {result.errors.map((e, i) => (
                      <li key={i}>Row {e.row}: {e.reason} {e.phone || ''}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={close} className="rounded-xl bg-white/60 border-white/60">Close</Button>
          <Button onClick={onUpload} disabled={!file || busy} className="gradient-orange text-white rounded-xl" data-testid="contacts-csv-upload-button">
            {busy && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
            Upload & Import
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ContactsPage() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [editTarget, setEditTarget] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [csvOpen, setCsvOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/contacts/', { params: q ? { q } : {} });
      setContacts(r.data || []);
    } catch {
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!q) return contacts;
    const s = q.toLowerCase();
    return contacts.filter((c) => (
      c.name?.toLowerCase().includes(s) ||
      c.phone?.toLowerCase().includes(s) ||
      c.email?.toLowerCase().includes(s)
    ));
  }, [contacts, q]);

  const onDelete = async (c) => {
    if (!window.confirm(`Delete contact "${c.name}"?`)) return;
    try {
      await api.delete(`/contacts/${c.id}`);
      toast.success('Deleted');
      load();
    } catch {
      toast.error('Delete failed');
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" data-testid="contacts-heading">Contacts</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage customers you'll send WhatsApp campaigns to.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setCsvOpen(true)} className="rounded-xl bg-white/60 border-white/60" data-testid="contacts-import-csv-button">
            <Upload className="h-4 w-4 mr-1.5" /> Import CSV
          </Button>
          <Button onClick={() => { setEditTarget(null); setDialogOpen(true); }} className="gradient-orange text-white rounded-xl" data-testid="contacts-add-contact-button">
            <Plus className="h-4 w-4 mr-1.5" /> Add Contact
          </Button>
        </div>
      </div>

      <div className="glass-card p-3 sm:p-4">
        <div className="relative max-w-md">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, phone, or email..." className="pl-9 bg-white/70 border-white/60 rounded-xl" data-testid="contacts-search-input" />
        </div>
      </div>

      <div className="glass-card overflow-hidden">
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-sm" data-testid="contacts-table">
            <thead className="bg-white/40 border-b border-white/50">
              <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Phone</th>
                <th className="px-4 py-3 hidden md:table-cell">Email</th>
                <th className="px-4 py-3 hidden lg:table-cell">Tags</th>
                <th className="px-4 py-3 hidden xl:table-cell">Groups</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="text-center py-10 text-muted-foreground"><Loader2 className="h-5 w-5 mx-auto animate-spin" /></td></tr>
              ) : !filtered.length ? (
                <tr><td colSpan={6} className="text-center py-10 text-muted-foreground">No contacts. Add one or import a CSV.</td></tr>
              ) : filtered.map((c) => (
                <tr key={c.id} className="border-b border-white/30 hover:bg-white/30 transition-colors">
                  <td className="px-4 py-3 font-medium" data-testid={`contact-row-name-${c.id}`}>{c.name}</td>
                  <td className="px-4 py-3 text-muted-foreground tabular-nums"><Phone className="inline h-3.5 w-3.5 mr-1" />{c.phone}</td>
                  <td className="px-4 py-3 text-muted-foreground hidden md:table-cell">{c.email ? (<><Mail className="inline h-3.5 w-3.5 mr-1" />{c.email}</>) : '—'}</td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    <div className="flex flex-wrap gap-1">
                      {(c.tags || []).map((t) => (
                        <span key={t} className="text-[10px] uppercase tracking-wide bg-[#FFE2D1]/70 text-[#9A3412] px-2 py-0.5 rounded-full"><Tag className="inline h-2.5 w-2.5 mr-0.5" />{t}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden xl:table-cell">
                    <div className="flex flex-wrap gap-1">
                      {(c.groups || []).map((g) => (
                        <span key={g.group_id} className="text-[10px] bg-white/70 px-2 py-0.5 rounded-full border border-white/60" style={{ color: g.color }}>{g.name}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button variant="ghost" size="icon" onClick={() => { setEditTarget(c); setDialogOpen(true); }} aria-label="Edit" data-testid={`contact-edit-${c.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => onDelete(c)} aria-label="Delete" data-testid={`contact-delete-${c.id}`}><Trash2 className="h-4 w-4 text-[#9F1239]" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ContactDialog open={dialogOpen} onOpenChange={setDialogOpen} contact={editTarget} onSaved={load} />
      <CSVImportDialog open={csvOpen} onOpenChange={setCsvOpen} onImported={load} />
    </div>
  );
}
